import React from 'react';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../config/locale';

type LanguageSelectorProps = {
  value: string;
  onChange: (value: string) => void;
  /** `storefront` = light bar for public customer header; default matches workspace header */
  variant?: 'workspace' | 'storefront';
  /** Full-width block (e.g. mobile drawer) */
  fullWidth?: boolean;
};

const FLAG_MAP: Record<string, string> = {
  en: '🇬🇧',
  fr: '🇫🇷',
  es: '🇪🇸',
  de: '🇩🇪',
};

const CODE_MAP: Record<string, string> = {
  en: 'ENG',
  fr: 'FR',
  es: 'ES',
  de: 'DE',
};

const WRAP: Record<NonNullable<LanguageSelectorProps['variant']>, string> = {
  workspace:
    'inline-flex items-center rounded-md border border-blue-600 bg-blue-800/70 px-1.5 py-px text-[10px] font-medium text-blue-50 shadow-sm',
  storefront:
    'inline-flex h-8 items-center rounded-full border border-slate-300 bg-white px-1.5 py-0 text-[10px] font-medium text-slate-700 shadow-sm',
};

const SELECT: Record<NonNullable<LanguageSelectorProps['variant']>, string> = {
  workspace: 'bg-transparent pr-2 text-[10px] font-medium text-blue-50 outline-none focus-visible:ring-0',
  storefront: 'bg-transparent pr-2 text-[10px] font-medium text-slate-800 outline-none focus-visible:ring-0',
};

function LanguageSelector({
  value,
  onChange,
  variant = 'workspace',
  fullWidth = false,
}: LanguageSelectorProps) {
  const { t } = useTranslation();
  const wrap = [
    WRAP[variant],
    fullWidth ? '!flex w-full min-w-0 [&_select]:w-full [&_select]:min-w-0' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const select = [SELECT[variant], fullWidth ? '!w-full' : ''].filter(Boolean).join(' ');
  return (
    <div className={wrap}>
      <select
        className={select}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={t('common.language')}
      >
        {LANGUAGES.map((lang) => {
          const flag = FLAG_MAP[lang.code] ?? '🌐';
          const code = CODE_MAP[lang.code] ?? lang.code.toUpperCase();
          return (
            <option
              key={lang.code}
              value={lang.code}
              className="bg-white text-slate-900"
            >
              {`${flag} ${code}`}
            </option>
          );
        })}
      </select>
    </div>
  );
}

export default LanguageSelector;

